import { useMutation, gql } from '@apollo/client';
import { toast } from 'react-toastify';

const SUBMIT_GUEST_RATING = gql`
  mutation SubmitGuestRating($input: SubmitGuestRatingInput!) {
    submitGuestRating(input: $input) {
      id
      bookingId
      guestRating {
        general {
          score
        }
        maintenance {
          score
          improve
        }
        cleanliness {
          score
          improve
        }
        communication {
          score
          improve
        }
        convenience {
          score
          improve
        }
        accuracy {
          score
          improve
        }
        comment
      }
      guestRatedAt
    }
  }
`;

const SUBMIT_HOST_RATING = gql`
  mutation SubmitHostRating($input: SubmitHostRatingInput!) {
    submitHostRating(input: $input) {
      id
      bookingId
      hostRating {
        general {
          score
        }
        comment
      }
      hostRatedAt
    }
  }
`;

const CAN_RATE_BOOKING = gql`
  query CanRateBooking($bookingId: ID!) {
    canRateBooking(bookingId: $bookingId) {
      canGuestRate
      canHostRate
      guestRatingDeadline
      hostRatingDeadline
      reason
    }
  }
`;

interface CategoryRating {
  score: number;
  improve?: string[];
}

interface GuestRatingInput {
  general: CategoryRating;
  maintenance: CategoryRating;
  cleanliness: CategoryRating;
  communication: CategoryRating;
  convenience: CategoryRating;
  accuracy: CategoryRating;
  comment?: string;
}

interface HostRatingInput {
  general: number;
  comment?: string;
}

export const useRatingMutations = () => {
  const [submitGuestRatingMutation, { loading: isSubmittingGuestRating }] =
    useMutation(SUBMIT_GUEST_RATING);
  const [submitHostRatingMutation, { loading: isSubmittingHostRating }] =
    useMutation(SUBMIT_HOST_RATING);

  const submitGuestRating = async (
    bookingId: string,
    rating: GuestRatingInput,
    onSuccess?: () => void,
  ) => {
    try {
      const input = {
        bookingId,
        rating: {
          general: { score: rating.general.score },
          maintenance: {
            score: rating.maintenance.score,
            improve: rating.maintenance.improve,
          },
          cleanliness: {
            score: rating.cleanliness.score,
            improve: rating.cleanliness.improve,
          },
          communication: {
            score: rating.communication.score,
            improve: rating.communication.improve,
          },
          convenience: {
            score: rating.convenience.score,
            improve: rating.convenience.improve,
          },
          accuracy: {
            score: rating.accuracy.score,
            improve: rating.accuracy.improve,
          },
          comment: rating.comment,
        },
      };

      await submitGuestRatingMutation({ variables: { input } });
      toast.success('Thank you for your rating!');
      onSuccess?.();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to submit rating. Please try again.');
      throw error;
    }
  };

  const submitHostRating = async (
    bookingId: string,
    rating: HostRatingInput,
    onSuccess?: () => void,
  ) => {
    try {
      const input = {
        bookingId,
        rating: {
          general: { score: rating.general },
          comment: rating.comment,
        },
      };

      await submitHostRatingMutation({ variables: { input } });
      toast.success('Thank you for your rating!');
      onSuccess?.();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to submit rating. Please try again.');
      throw error;
    }
  };

  return {
    submitGuestRating,
    submitHostRating,
    isSubmittingGuestRating,
    isSubmittingHostRating,
  };
};

export { CAN_RATE_BOOKING };
